/***
*
*   useFeedback
*   Owns all data-fetching, filter, sort, and pagination logic for the Feedback view.
*   Returns derived display state and action callbacks; no JSX.
*
**********/

import { useState, useEffect, useRef, useContext } from 'react';
import { ViewContext, useAPI } from 'components/lib';
import { FEEDBACK_FILTER_DEFAULTS, TEXT } from './constants';

// ─── Filter helpers ───────────────────────────────────────────────────────────

function countActiveFilters(f) {
  let n = 0;
  if (f.ratings.length > 0)                n++;
  if (f.dateFrom !== '' || f.dateTo !== '') n++;
  if (f.hasMessage !== 'all')               n++;
  return n;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useFeedback() {
  const { modal } = useContext(ViewContext);

  // Pagination
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(50);
  const [total,      setTotal]      = useState(0);

  // Sorting
  const [sortKey, setSortKey] = useState('date_created');
  const [sortDir, setSortDir] = useState('desc');

  // Search
  const [search, setSearch] = useState('');

  // Filter panel
  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const [draftFilters,   setDraftFilters]   = useState(FEEDBACK_FILTER_DEFAULTS);
  const [appliedFilters, setAppliedFilters] = useState(FEEDBACK_FILTER_DEFAULTS);
  const filtersRef = useRef(null);

  // refreshKey changes the URL so useAPI re-fetches after a mutation
  const [refreshKey, setRefreshKey] = useState(0);

  const offset = (page - 1) * pageSize;

  const params = new URLSearchParams({
    offset:  String(offset),
    limit:   String(pageSize),
    sortKey,
    sortDir,
    _r:      String(refreshKey),
  });
  if (search.trim())                         params.set('search',     search.trim());
  if (appliedFilters.ratings.length)         params.set('ratings',    appliedFilters.ratings.join(','));
  if (appliedFilters.dateFrom)               params.set('dateFrom',   appliedFilters.dateFrom);
  if (appliedFilters.dateTo)                 params.set('dateTo',     appliedFilters.dateTo);
  if (appliedFilters.hasMessage !== 'all')   params.set('hasMessage', appliedFilters.hasMessage);

  const feedback = useAPI(`/api/feedback?${params.toString()}`);
  const metrics  = useAPI(`/api/feedback/metrics?_r=${refreshKey}`);

  useEffect(() => {
    if (feedback.data?.total !== undefined) setTotal(feedback.data.total);
  }, [feedback.data]);

  // Close filter panel on outside click
  useEffect(() => {
    if (!filtersOpen) return;
    function handler(e) {
      if (filtersRef.current && !filtersRef.current.contains(e.target))
        setFiltersOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filtersOpen]);

  // ─── Filter panel actions ─────────────────────────────────────────────────

  function openFilters() {
    setDraftFilters(appliedFilters);
    setFiltersOpen(true);
  }

  function closeFilters() {
    setFiltersOpen(false);
  }

  function applyFiltersAction() {
    setAppliedFilters(draftFilters);
    setFiltersOpen(false);
    setPage(1);
  }

  function clearFilters() {
    setDraftFilters(FEEDBACK_FILTER_DEFAULTS);
    setAppliedFilters(FEEDBACK_FILTER_DEFAULTS);
    setFiltersOpen(false);
    setPage(1);
  }

  // ─── Derived rows ─────────────────────────────────────────────────────────

  const displayRows = feedback.data?.results || [];
  const totalPages  = Math.max(1, Math.ceil(total / pageSize));

  // ─── Sort ─────────────────────────────────────────────────────────────────

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  }

  function handleSetSearch(value) {
    setSearch(value);
    setPage(1);
  }

  function handleSetPageSize(value) {
    setPageSize(value);
    setPage(1);
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  function deleteFeedback(row) {
    modal.show({
      title:       TEXT.deleteTitle,
      form:        { id: { type: 'hidden', value: row.id } },
      buttonText:  TEXT.deleteBtn,
      text:        TEXT.deleteBody,
      url:         '/api/feedback',
      method:      'DELETE',
      destructive: true,
    }, () => {
      setTotal(t => {
        const newTotal = Math.max(0, t - 1);
        const newTotalPages = Math.max(1, Math.ceil(newTotal / pageSize));
        setPage(p => Math.min(p, newTotalPages));
        return newTotal;
      });
      setRefreshKey(k => k + 1);
    });
  }

  return {
    // Data
    displayRows,
    metricsData:    metrics.data?.data || metrics.data || {},
    loading:        feedback.loading,
    metricsLoading: metrics.loading,

    // Pagination
    page, setPage,
    pageSize, setPageSize: handleSetPageSize,
    totalPages,

    // Sorting
    sortKey, sortDir, toggleSort,

    // Search
    search, setSearch: handleSetSearch,

    // Filter panel
    filtersOpen, filtersRef,
    draftFilters, setDraftFilters,
    activeFilterCount: countActiveFilters(appliedFilters),
    openFilters, closeFilters,
    applyFilters: applyFiltersAction,
    clearFilters,

    // Row actions
    deleteFeedback,
  };
}
